'use client'

/**
 * CapsWindowEditor — per-account safety caps + send-window editor (spec §11).
 *
 * Pre-fills the §11 safe defaults, shows a "Safe / Aggressive" helper hint as a
 * cap moves toward the safe ceiling, and surfaces a subtle warning when a cap is
 * raised past the safe zone — it never blocks (the server clamps above-ceiling
 * values for non-admins). Used by the Accounts and Caps & Safety tabs.
 */

import { useMemo, useState } from 'react'
import { Input, Button } from '@/components/ui-v2'
import { AlertTriangle, Check } from 'lucide-react'
import {
  DEFAULT_CAPS,
  SAFE_CAP_CEILING,
  DEFAULT_SEND_WINDOW,
  DAY_LABELS,
  TIMEZONE_OPTIONS,
  type CapProfileKey,
} from '../constants'
import type { OutreachAccount, AccountCaps, SendWindow, UpdateAccountPayload } from '../types'
import { getUserTimezone, withSelectedTimezone } from '../user-timezone'

type Props = {
  account: OutreachAccount
  onSave: (payload: UpdateAccountPayload) => Promise<void>
}

function profileKey(account: OutreachAccount): CapProfileKey {
  if (account.channel === 'linkedin') return 'linkedin_message'
  if (account.channel === 'whatsapp') return 'whatsapp'
  return 'email'
}

/** safe → within default · caution → between default and ceiling · over → above ceiling. */
function capZone(value: number, def: number, ceiling: number): 'safe' | 'caution' | 'over' {
  if (value > ceiling) return 'over'
  if (value > def) return 'caution'
  return 'safe'
}

export function CapsWindowEditor({ account, onSave }: Props) {
  const key = profileKey(account)
  const defaults = DEFAULT_CAPS[key]
  const ceiling = SAFE_CAP_CEILING[key]
  const isLinkedIn = account.channel === 'linkedin'
  const userTimezone = useMemo(getUserTimezone, [])
  const defaultSendWindow = useMemo(
    () => ({ ...DEFAULT_SEND_WINDOW, tz: userTimezone }),
    [userTimezone],
  )

  const [caps, setCaps] = useState<AccountCaps>({ ...defaults, ...account.caps })
  const [inviteCaps, setInviteCaps] = useState<AccountCaps>(
    account.inviteCaps ?? { ...DEFAULT_CAPS.linkedin_invite },
  )
  const [inmailCaps, setInmailCaps] = useState<AccountCaps>(
    account.inmailCaps ?? { ...DEFAULT_CAPS.linkedin_inmail },
  )
  const [window, setWindow] = useState<SendWindow>({
    ...defaultSendWindow,
    ...account.sendWindow,
  })
  const [sendAnytime, setSendAnytime] = useState<boolean>(!!account.sendAnytime)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const dailyZone = capZone(caps.daily, defaults.daily, ceiling.daily)
  const hourlyZone = capZone(caps.hourly, defaults.hourly, ceiling.hourly)
  const inviteDailyZone = capZone(
    inviteCaps.daily,
    DEFAULT_CAPS.linkedin_invite.daily,
    SAFE_CAP_CEILING.linkedin_invite.daily,
  )
  const inmailDailyZone = capZone(
    inmailCaps.daily,
    DEFAULT_CAPS.linkedin_inmail.daily,
    SAFE_CAP_CEILING.linkedin_inmail.daily,
  )
  const aboveSafe =
    dailyZone === 'over' ||
    hourlyZone === 'over' ||
    (isLinkedIn && (inviteDailyZone === 'over' || inmailDailyZone === 'over'))

  const dirty = useMemo(() => {
    return (
      JSON.stringify(caps) !== JSON.stringify({ ...defaults, ...account.caps }) ||
      JSON.stringify(window) !== JSON.stringify({ ...defaultSendWindow, ...account.sendWindow }) ||
      sendAnytime !== !!account.sendAnytime ||
      (isLinkedIn &&
        JSON.stringify(inviteCaps) !==
          JSON.stringify(account.inviteCaps ?? { ...DEFAULT_CAPS.linkedin_invite })) ||
      (isLinkedIn &&
        JSON.stringify(inmailCaps) !==
          JSON.stringify(account.inmailCaps ?? { ...DEFAULT_CAPS.linkedin_inmail }))
    )
  }, [caps, window, sendAnytime, inviteCaps, inmailCaps, account, defaults, defaultSendWindow, isLinkedIn])

  const timezoneOptions = withSelectedTimezone(TIMEZONE_OPTIONS, window.tz)

  const num = (v: string, fallback: number) => {
    const n = Number(v)
    return Number.isFinite(n) && n >= 0 ? n : fallback
  }

  const toggleDay = (day: number) => {
    setWindow((w) => {
      const has = w.days.includes(day)
      const days = has ? w.days.filter((d) => d !== day) : [...w.days, day].sort()
      return { ...w, days }
    })
  }

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    try {
      const payload: UpdateAccountPayload = { caps, sendWindow: window, sendAnytime }
      if (isLinkedIn) {
        payload.inviteCaps = inviteCaps
        payload.inmailCaps = inmailCaps
      }
      await onSave(payload)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } finally {
      setSaving(false)
    }
  }

  const handleResetDefaults = () => {
    setCaps({ ...defaults })
    setInviteCaps({ ...DEFAULT_CAPS.linkedin_invite })
    setInmailCaps({ ...DEFAULT_CAPS.linkedin_inmail })
    setWindow({ ...defaultSendWindow })
    setSendAnytime(false)
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Caps */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>
            {isLinkedIn ? 'Message caps' : 'Sending caps'}
          </span>
          <CapHint zone={dailyZone === 'over' || hourlyZone === 'over' ? 'over' : dailyZone === 'caution' || hourlyZone === 'caution' ? 'caution' : 'safe'} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <CapField
            label="Per day"
            value={caps.daily}
            zone={dailyZone}
            safeMax={defaults.daily}
            ceiling={ceiling.daily}
            onChange={(v) => setCaps((c) => ({ ...c, daily: num(v, c.daily) }))}
          />
          <CapField
            label="Per hour"
            value={caps.hourly}
            zone={hourlyZone}
            safeMax={defaults.hourly}
            ceiling={ceiling.hourly}
            onChange={(v) => setCaps((c) => ({ ...c, hourly: num(v, c.hourly) }))}
          />
          <Input
            label="Min gap (seconds)"
            type="number"
            min={0}
            value={caps.minDelaySeconds}
            onChange={(e) => setCaps((c) => ({ ...c, minDelaySeconds: num(e.target.value, c.minDelaySeconds) }))}
            hint={`Safe: ${defaults.minDelaySeconds}s`}
          />
          <Input
            label="Jitter (seconds)"
            type="number"
            min={0}
            value={caps.jitter}
            onChange={(e) => setCaps((c) => ({ ...c, jitter: num(e.target.value, c.jitter) }))}
            hint={`Randomizes the gap by up to this much`}
          />
        </div>
        <p className="text-[12px] mt-1.5" style={{ color: 'var(--text-tertiary)' }}>
          Setting a daily or hourly cap to 0 pauses this account — nothing sends until you raise it.
        </p>
      </div>

      {/* LinkedIn invite caps — separate, lower limits */}
      {isLinkedIn && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>
              Invite caps
            </span>
            <CapHint zone={inviteDailyZone} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <CapField
              label="Invites / day"
              value={inviteCaps.daily}
              zone={inviteDailyZone}
              safeMax={DEFAULT_CAPS.linkedin_invite.daily}
              ceiling={SAFE_CAP_CEILING.linkedin_invite.daily}
              onChange={(v) => setInviteCaps((c) => ({ ...c, daily: num(v, c.daily) }))}
            />
            <CapField
              label="Invites / hour"
              value={inviteCaps.hourly}
              zone={capZone(inviteCaps.hourly, DEFAULT_CAPS.linkedin_invite.hourly, SAFE_CAP_CEILING.linkedin_invite.hourly)}
              safeMax={DEFAULT_CAPS.linkedin_invite.hourly}
              ceiling={SAFE_CAP_CEILING.linkedin_invite.hourly}
              onChange={(v) => setInviteCaps((c) => ({ ...c, hourly: num(v, c.hourly) }))}
            />
          </div>
          <p className="text-[12px] mt-1.5" style={{ color: 'var(--text-tertiary)' }}>
            ~20 invites/day ≈ ~100/week — under LinkedIn&apos;s safe zone.
          </p>
        </div>
      )}

      {/* InMail caps are independent from both ordinary messages and invites. */}
      {isLinkedIn && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>
              InMail caps
            </span>
            <CapHint zone={inmailDailyZone} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <CapField
              label="InMails / day"
              value={inmailCaps.daily}
              zone={inmailDailyZone}
              safeMax={DEFAULT_CAPS.linkedin_inmail.daily}
              ceiling={SAFE_CAP_CEILING.linkedin_inmail.daily}
              onChange={(v) => setInmailCaps((c) => ({ ...c, daily: num(v, c.daily) }))}
            />
            <CapField
              label="InMails / hour"
              value={inmailCaps.hourly}
              zone={capZone(
                inmailCaps.hourly,
                DEFAULT_CAPS.linkedin_inmail.hourly,
                SAFE_CAP_CEILING.linkedin_inmail.hourly,
              )}
              safeMax={DEFAULT_CAPS.linkedin_inmail.hourly}
              ceiling={SAFE_CAP_CEILING.linkedin_inmail.hourly}
              onChange={(v) => setInmailCaps((c) => ({ ...c, hourly: num(v, c.hourly) }))}
            />
          </div>
          <p className="text-[12px] mt-1.5" style={{ color: 'var(--text-tertiary)' }}>
            Credits are checked separately immediately before a new InMail thread is opened.
          </p>
        </div>
      )}

      {/* Send window */}
      <div>
        <span className="text-[13px] font-medium mb-2 block" style={{ color: 'var(--text-primary)' }}>
          Send window
        </span>
        <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={sendAnytime}
            onChange={(e) => setSendAnytime(e.target.checked)}
            style={{ accentColor: 'var(--accent-emphasis)', width: 15, height: 15 }}
          />
          <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>
            Send at any time
          </span>
          <span className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
            — ignore business hours (caps still apply)
          </span>
        </label>
        {sendAnytime ? (
          <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
            This account sends at any time of day. Caps and the minimum gap between sends still apply.
          </p>
        ) : (
          <>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {DAY_LABELS.map((label, day) => {
            const active = window.days.includes(day)
            return (
              <button
                key={label}
                onClick={() => toggleDay(day)}
                className="h-7 px-2.5 rounded-md text-[12px] font-medium transition-colors"
                style={{
                  background: active ? 'var(--accent-muted)' : 'var(--surface-tertiary)',
                  color: active ? 'var(--accent-emphasis)' : 'var(--text-tertiary)',
                  border: `1px solid ${active ? 'var(--border-accent)' : 'transparent'}`,
                }}
              >
                {label}
              </button>
            )
          })}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Start"
            type="time"
            value={window.start}
            onChange={(e) => setWindow((w) => ({ ...w, start: e.target.value }))}
          />
          <Input
            label="End"
            type="time"
            value={window.end}
            onChange={(e) => setWindow((w) => ({ ...w, end: e.target.value }))}
          />
          <div>
            <label className="text-[13px] font-medium mb-1.5 block" style={{ color: 'var(--text-primary)' }}>
              Timezone
            </label>
            <select
              value={window.tz}
              onChange={(e) => setWindow((w) => ({ ...w, tz: e.target.value }))}
              className="w-full h-9 rounded-lg px-3 text-[13px] outline-none"
              style={{
                border: '1px solid var(--border-default)',
                background: 'var(--surface-primary)',
                color: 'var(--text-primary)',
              }}
            >
              {timezoneOptions.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>
        </div>
          </>
        )}
      </div>

      {/* Above-safe warning — informs, never blocks */}
      {aboveSafe && (
        <div
          className="flex items-start gap-2 p-3 rounded-lg"
          style={{ background: 'var(--bg-warning)', color: 'var(--fg-warning)' }}
        >
          <AlertTriangle size={15} strokeWidth={2} className="mt-0.5 shrink-0" />
          <p className="text-[12px] leading-relaxed">
            One or more caps are above the safe zone. Higher volume raises the risk of a
            provider ban. Workspace admins can run above the safe ceiling; otherwise these
            values are clamped on save.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          onClick={handleResetDefaults}
          className="text-[12px] font-medium"
          style={{ color: 'var(--text-tertiary)' }}
        >
          Reset to safe defaults
        </button>
        <div className="flex items-center gap-2">
          {saved && (
            <span className="flex items-center gap-1 text-[12px]" style={{ color: 'var(--fg-success)' }}>
              <Check size={13} strokeWidth={2.5} /> Saved
            </span>
          )}
          <Button variant="primary" size="md" disabled={!dirty || saving} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  )
}

/* ─── Sub-pieces ─── */

function CapHint({ zone }: { zone: 'safe' | 'caution' | 'over' }) {
  const meta = {
    safe: { label: 'Safe', color: 'var(--fg-success)' },
    caution: { label: 'Aggressive', color: 'var(--fg-warning)' },
    over: { label: 'Above safe zone', color: 'var(--fg-danger)' },
  }[zone]
  return (
    <span className="flex items-center gap-1 text-[11px] font-medium" style={{ color: meta.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
      {meta.label}
    </span>
  )
}

function CapField({
  label,
  value,
  zone,
  safeMax,
  ceiling,
  onChange,
}: {
  label: string
  value: number
  zone: 'safe' | 'caution' | 'over'
  safeMax: number
  ceiling: number
  onChange: (v: string) => void
}) {
  const hint =
    value === 0
      ? 'Paused — 0 blocks all sends'
      : zone === 'over'
        ? `Above safe ceiling (${ceiling})`
        : zone === 'caution'
          ? `Safe up to ${safeMax}`
          : `Safe: ${safeMax}`
  return (
    <Input
      label={label}
      type="number"
      min={0}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      hint={hint}
    />
  )
}
